// จัดการการเช็คขาดสอบ — ผู้ดูแลระบบ (99) ลบการเช็คที่ครูกดผิดวิชา/ผิดห้องทั้งชุด
// เช่น ครูเช็ค "ภาษาไทย ม.2/12" ทั้งที่สอบคณิตศาสตร์ — ลบแล้ววิชานั้นกลับไปอยู่ในตัวเลือกของ
// หน้าเช็ค ให้เช็ควิชาที่ถูกใหม่ (server ตรวจสิทธิ์ซ้ำ และลง audit exam.absence.delete พร้อมเหตุผล)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { toISODate } from '../utils/thaiDate';
import { API, apiError, dayLabel, eventName, eventRange, pickEvent, useExamEvents } from './examAbsenceUtils';

export default function ExamAbsenceManage() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const { loading: evLoading, events, error: evError } = useExamEvents();
  const today = toISODate(new Date());
  const ev = useMemo(() => pickEvent(events, params.get('cal'), today), [events, params, today]);
  const dateParam = params.get('date');
  const date = ev && ev.days.includes(dateParam) ? dateParam : null;

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!ev) return undefined;
    let alive = true;
    setRows(null);
    setError(null);
    axiosInstance.get(`${API}/exam-absence/sittings`, { params: { cal_id: ev.cal_id, date: date || undefined } })
      .then(({ data }) => alive && setRows(data.rows))
      .catch((err) => alive && setError(apiError(err)));
    return () => { alive = false; };
  }, [ev, date]);

  useEffect(() => load(), [load]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, String(v));
    }
    setParams(next);
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!rows) return [];
    if (!q) return rows;
    return rows.filter((r) => [r.room_label, r.subject_label, r.checked_by_name, r.updated_by_name]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const openDelete = (row) => { setTarget(row); setReason(''); };

  const confirmDelete = async () => {
    if (!target || !reason.trim()) return;
    setBusy(true);
    try {
      await axiosInstance.delete(`${API}/exam-absence/sittings/${target.eas_id}`, {
        data: { cal_id: ev.cal_id, reason: reason.trim() },
      });
      Swal.fire({ icon: 'success', title: 'ลบแล้ว', text: `${target.subject_label} ห้อง ${target.room_label}`, timer: 1800, showConfirmButton: false });
      setRows((prev) => (prev || []).filter((r) => r.eas_id !== target.eas_id));
      setTarget(null);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: apiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">จัดการการเช็คขาดสอบ</h4>
          <div className="fs-xs text-secondary">
            ลบการเช็คที่ครูกดผิดวิชา/ผิดห้องทั้งชุด — ลบแล้ววิชานั้นกลับไปให้เช็คใหม่ได้ที่หน้าเช็คนักเรียนขาดสอบ
          </div>
        </div>

        {evLoading && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}
        {evError && <Alert variant="danger">{evError}</Alert>}
        {!evLoading && !evError && !ev && <Alert variant="secondary">ยังไม่มีช่วงสอบในปฏิทิน</Alert>}

        {ev && (
          <Card className="card-one mb-3">
            <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-2">
              <Form.Select size="sm" style={{ maxWidth: 340 }} value={ev.cal_id}
                onChange={(e) => update({ cal: e.target.value, date: null })}>
                {events.map((e) => <option key={e.cal_id} value={e.cal_id}>{eventName(e)} ({eventRange(e)})</option>)}
              </Form.Select>
              <ButtonGroup size="sm" className="flex-wrap">
                <Button variant={!date ? 'primary' : 'outline-primary'} onClick={() => update({ date: null })}>ทุกวัน</Button>
                {ev.days.map((d) => (
                  <Button key={d} variant={d === date ? 'primary' : 'outline-primary'} onClick={() => update({ date: d })}>
                    {dayLabel(d)}{d === today ? ' (วันนี้)' : ''}
                  </Button>
                ))}
              </ButtonGroup>
              <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="ค้นหาห้อง / วิชา / ผู้เช็ค"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </Card.Body>
          </Card>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
        {ev && !rows && !error && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}

        {rows && (
          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">
                {eventName(ev)} · {date ? dayLabel(date, { short: false }) : 'ทุกวัน'}
              </Card.Title>
              <Badge bg="secondary" className="ms-auto">{shown.length} รายการ</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    {!date && <th>วันสอบ</th>}
                    <th>ห้อง</th>
                    <th>วิชา</th>
                    <th className="text-end">นักเรียน</th>
                    <th className="text-end">ขาด</th>
                    <th className="text-end">ลา</th>
                    <th className="text-end">สาย</th>
                    <th>ผู้เช็ค</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr><td colSpan={9} className="text-center text-secondary py-4">
                      {rows.length ? 'ไม่พบตามคำค้น' : 'ยังไม่มีการเช็คในช่วงนี้'}
                    </td></tr>
                  ) : shown.map((r) => (
                    <tr key={r.eas_id}>
                      {!date && <td className="text-nowrap">{dayLabel(r.date)}</td>}
                      <td className="text-nowrap">{r.room_label}</td>
                      <td className="fs-sm">{r.subject_label}</td>
                      <td className="text-end">{r.total}</td>
                      <td className="text-end">{r.absent ? <span className="text-danger fw-semibold">{r.absent}</span> : '–'}</td>
                      <td className="text-end">{r.on_leave || '–'}</td>
                      <td className="text-end">{r.late || '–'}</td>
                      <td className="fs-sm">
                        {r.checked_by_name}
                        <small className="d-block text-secondary">
                          {r.checked_time} น.
                          {r.updated_time && ` · แก้ ${r.updated_time} น.${r.updated_by_name && r.updated_by_name !== r.checked_by_name ? ` โดย ${r.updated_by_name}` : ''}`}
                        </small>
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-danger" onClick={() => openDelete(r)}>
                          <i className="ri-delete-bin-line"></i> ลบ
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        <Modal show={!!target} onHide={() => !busy && setTarget(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">ลบการเช็คขาดสอบ</Modal.Title>
          </Modal.Header>
          {target && (
            <Modal.Body>
              <p className="mb-2">
                <strong>{target.subject_label}</strong> ห้อง <strong>{target.room_label}</strong> · {dayLabel(target.date)}
                <br />
                <span className="fs-sm text-secondary">
                  เช็คโดย {target.checked_by_name} · นักเรียน {target.total} คน (ขาด {target.absent} ลา {target.on_leave} สาย {target.late})
                </span>
              </p>
              <Alert variant="warning" className="fs-sm py-2">
                ผลการเช็คของทุกคนในห้องนี้สำหรับวิชานี้จะถูกลบ แล้ววิชานี้กลับไปให้เช็คใหม่ได้ —
                ครูทั่วไปเช็คได้เฉพาะวันนี้ ถ้าเป็นวันสอบที่ผ่านไปแล้ว งานวัดผลต้องเป็นคนเช็คแทน
              </Alert>
              <Form.Label className="fs-sm">เหตุผลที่ลบ <span className="text-danger">*</span></Form.Label>
              <Form.Control as="textarea" rows={2} maxLength={120} value={reason} autoFocus
                placeholder="เช่น ครูเช็คผิดวิชา — ห้องนี้สอบคณิตศาสตร์"
                onChange={(e) => setReason(e.target.value)} />
            </Modal.Body>
          )}
          <Modal.Footer>
            <Button variant="secondary" disabled={busy} onClick={() => setTarget(null)}>ยกเลิก</Button>
            <Button variant="danger" disabled={busy || !reason.trim()} onClick={confirmDelete}>
              {busy ? <Spinner animation="border" size="sm" /> : 'ลบการเช็ค'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </>
  );
}
